import clsx from "clsx";
import { dayKeyForDate } from "../../utils";
import { AppWindow, ChevronLeft, ChevronRight, Send } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Process } from "../../api/types";
import type { ProcessInstance } from "./processGraphModel";
import { appIconUri } from "./appIcon";
import { runApps } from "./processSearch";
import {
  type InstanceScope,
  type InstanceScopeIndex,
  type InstanceScopeQuery,
  type ScopeQueryStatus,
  buildInstanceScopeIndex,
  lastDaysFrom,
  matchInstanceScope,
  scopeDayBounds,
  scopeNodeOptions,
  scopeUserOptions,
  withoutScopeCriterion,
} from "./processScopeModel";

const OptionRow = ({
  checked,
  label,
  hint,
  icon,
  onClick,
}: {
  checked: boolean;
  label: string;
  hint?: string;
  icon?: React.ReactNode;
  onClick: () => void;
}) => (
  <label className="dropdown-option w-full gap-3 has-[:focus-visible]:bg-muted-surface">
    <input
      type="checkbox"
      checked={checked}
      onChange={onClick}
      className="h-4 w-4 shrink-0 accent-info"
    />
    {icon}
    <span title={label} className="min-w-0 flex-1 truncate text-sm text-text">
      {label}
    </span>
    {hint && <span className="shrink-0 text-xs tabular-nums text-text-dim">{hint}</span>}
  </label>
);

/** The right-aligned confirm button every criterion editor ends with. */
const EditorFooter = ({
  onPicked,
  confirmLabel,
}: {
  onPicked: () => void;
  confirmLabel?: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-end">
      <button
        type="button"
        onClick={onPicked}
        className="rounded-md px-3 py-1.5 text-sm font-medium text-info outline-none hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-info/20"
      >
        {confirmLabel ?? t("scope_filter_done")}
      </button>
    </div>
  );
};

type ScopeOption = {
  id: string;
  label: string;
  hint?: string;
  icon?: React.ReactNode;
};

/** The three multi-select filters share search, checkbox rows and selection behavior. */
const OptionsEditor = ({
  options,
  selected,
  onToggle,
  searchLabel,
  onPicked,
  confirmLabel,
}: {
  options: ScopeOption[];
  selected: string[];
  onToggle: (id: string) => void;
  searchLabel: string;
  onPicked: () => void;
  confirmLabel?: string;
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  const visible = options.filter(option =>
    option.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  return (
    <div>
      <div className="p-1 pb-2">
        <input
          ref={input}
          type="search"
          aria-label={searchLabel}
          placeholder={searchLabel}
          value={search}
          onChange={event => setSearch(event.target.value)}
          className="w-full rounded-md border border-outline bg-surface px-3 py-2 text-sm text-text outline-none placeholder:text-text-dim focus:border-info"
        />
      </div>
      <div className="max-h-64 overflow-y-auto">
        {visible.length === 0 && (
          <p className="px-3 py-4 text-center text-sm text-text-dim">
            {t("no_results_match_filters")}
          </p>
        )}
        {visible.map(option => (
          <OptionRow
            key={option.id}
            label={option.label}
            hint={option.hint}
            icon={option.icon}
            checked={selected.includes(option.id)}
            onClick={() => onToggle(option.id)}
          />
        ))}
      </div>
      <div className="mt-1 border-t border-outline p-2">
        <EditorFooter onPicked={onPicked} confirmLabel={confirmLabel} />
      </div>
    </div>
  );
};

const toggleValue = (values: string[], id: string) =>
  values.includes(id) ? values.filter(value => value !== id) : [...values, id];

type ScopeEditorProps = {
  scope: InstanceScope;
  onChange: (scope: InstanceScope) => void;
  onPicked: () => void;
  confirmLabel?: string;
};

export const UserEditor = ({
  instances,
  scope,
  onChange,
  ...props
}: ScopeEditorProps & { instances: ProcessInstance[] }) => {
  const { t } = useTranslation();
  const options = useMemo(() => scopeUserOptions(instances), [instances]);
  return (
    <OptionsEditor
      {...props}
      options={options.map(option => ({
        id: option.userId,
        label: option.userName ?? t("user"),
        hint: t("n_instances", { count: option.instanceCount }),
      }))}
      selected={scope.userIds}
      onToggle={id => onChange({ ...scope, userIds: toggleValue(scope.userIds, id) })}
      searchLabel={t("search_people")}
    />
  );
};

export const NodeEditor = ({
  process,
  scope,
  onChange,
  ...props
}: ScopeEditorProps & { process: Process }) => {
  const { t } = useTranslation();
  const options = useMemo(() => scopeNodeOptions(process), [process]);
  return (
    <OptionsEditor
      {...props}
      options={options.map(option => ({
        ...option,
        hint: t("n_instances", { count: option.instanceCount }),
      }))}
      selected={scope.nodeIds}
      onToggle={id => onChange({ ...scope, nodeIds: toggleValue(scope.nodeIds, id) })}
      searchLabel={t("search_steps")}
    />
  );
};

export const AppsEditor = ({
  instances,
  scope,
  onChange,
  ...props
}: ScopeEditorProps & { instances: ProcessInstance[] }) => {
  const { t } = useTranslation();
  const apps = useMemo(() => {
    // App counts reflect the other filters; keep zero-count options available so
    // an existing app selection can still be changed or cleared.
    const matching = new Set(
      matchInstanceScope(buildInstanceScopeIndex(instances), {
        ...withoutScopeCriterion(scope, "apps"),
        excludedIds: [],
      }).matchedIds,
    );
    const counts = new Map<string, number>();
    for (const instance of instances) {
      for (const app of runApps(instance))
        counts.set(app, (counts.get(app) ?? 0) + (matching.has(instance.id) ? 1 : 0));
    }
    return [...counts].sort(([a], [b]) => a.localeCompare(b));
  }, [instances, scope]);
  return (
    <OptionsEditor
      {...props}
      options={apps.map(([app, count]) => {
        const icon = appIconUri(app);
        return {
          id: app,
          label: app,
          hint: t("n_instances", { count }),
          icon: icon ? (
            <img src={icon} alt="" className="h-4 w-4 shrink-0 object-contain" />
          ) : (
            <AppWindow aria-hidden="true" className="h-4 w-4 shrink-0 text-text-dim" />
          ),
        };
      })}
      selected={scope.apps ?? []}
      onToggle={id => onChange({ ...scope, apps: toggleValue(scope.apps ?? [], id) })}
      searchLabel={t("processPage.searchApps")}
    />
  );
};

const DATE_PRESETS = [7, 30, 90];

/** One dot per recording on a day, up to this many — past it the day is simply "a busy one". */
const MAX_DAY_DOTS = 3;

const parseDayKey = (dayKey: string): Date => new Date(`${dayKey}T00:00:00`);

/** Monday-first cells for one month, padded with nulls so every row is a full week. */
const monthGrid = (year: number, month: number): (string | null)[] => {
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;
  const length = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = Array(lead).fill(null);
  for (let day = 1; day <= length; day++) cells.push(dayKeyForDate(new Date(year, month, day)));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const weekdayInitials = (): string[] => {
  const format = new Intl.DateTimeFormat(undefined, { weekday: "narrow" });
  // 1 Jan 2024 was a Monday, which is where the grid starts.
  return Array.from({ length: 7 }, (_, offset) => format.format(new Date(2024, 0, 1 + offset)));
};

const formatDayKey = (dayKey: string): string =>
  parseDayKey(dayKey).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

// A "from" box, a "to" box, and one calendar that fills whichever of them is being edited. The
// boxes are ours rather than `<input type="date">`: the native overlay is drawn outside the
// document, so its clicks read as clicks outside this popover, and its own month navigation and
// disabled-range rendering are not ours to fix.
export const DateEditor = ({
  index,
  scope,
  onChange,
  onPicked,
  confirmLabel,
}: {
  index: InstanceScopeIndex;
  scope: InstanceScope;
  onChange: (scope: InstanceScope) => void;
  onPicked: () => void;
  confirmLabel?: string;
}) => {
  const { t } = useTranslation();
  const bounds = scopeDayBounds(index);
  const today = dayKeyForDate(new Date());
  // "To" left open means "up to now", so that is what the box shows rather than sitting blank.
  const to = scope.to ?? today;
  // Which end the next click on the grid fills, and — because the grid is only shown while one of
  // them is being answered — whether there is a calendar at all. Null on open: the presets are the
  // common case, and a calendar nobody asked for buries them under a month of numbers.
  const [end, setEnd] = useState<"from" | "to" | null>(null);

  const recorded = useMemo(() => {
    const tally = new Map<string, number>();
    for (const day of index.dayById.values()) tally.set(day, (tally.get(day) ?? 0) + 1);
    return tally;
  }, [index.dayById]);

  const [view, setView] = useState(() => {
    const anchor = parseDayKey(scope.from ?? scope.to ?? bounds?.last ?? today);
    return { year: anchor.getFullYear(), month: anchor.getMonth() };
  });
  const cells = useMemo(() => monthGrid(view.year, view.month), [view]);
  const weekdays = useMemo(weekdayInitials, []);
  const shiftMonth = (delta: number) =>
    setView(current => {
      const shifted = new Date(current.year, current.month + delta, 1);
      return { year: shifted.getFullYear(), month: shifted.getMonth() };
    });

  const showMonthOf = (dayKey: string) => {
    const anchor = parseDayKey(dayKey);
    setView({ year: anchor.getFullYear(), month: anchor.getMonth() });
  };

  // Opening an end shows the month it already holds: the box says "4 Aug" and the grid answering it
  // has to be August, or the click that follows lands in whatever month was left over. It is the
  // month of what the *box* reads — an empty "to" says today, so today is where the grid opens.
  const openEnd = (which: "from" | "to") => {
    setEnd(which);
    showMonthOf(which === "from" ? (scope.from ?? bounds?.first ?? today) : to);
  };

  // With a month on screen the arrow keys are the fastest way through it, and nothing else here
  // wants them — the two text-entry filters live in other editors.
  // Anywhere that is not the calendar collapses it, the same gesture that dismisses the filter
  // editor one level up — the boxes are excused so that reopening one is a single click.
  const calendarRef = useRef<HTMLDivElement>(null);
  const boxRowRef = useRef<HTMLDivElement>(null);
  // Collapsing on *click* rather than mousedown matters: the calendar is tall, so removing it on
  // mousedown moves everything under it — including "Apply" — out from under the cursor before
  // mouseup, and the click never reaches the button it was aimed at. Waiting for the click lets
  // that button run first, and only then does the grid fold away.
  useEffect(() => {
    if (!end) return;
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (calendarRef.current?.contains(target) || boxRowRef.current?.contains(target)) return;
      setEnd(null);
    };
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, [end]);

  const shiftMonthRef = useRef(shiftMonth);
  shiftMonthRef.current = shiftMonth;
  useEffect(() => {
    if (!end) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      )
        return;
      event.preventDefault();
      shiftMonthRef.current(event.key === "ArrowLeft" ? -1 : 1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [end]);

  const setRange = (from: string | null, next: string | null) =>
    onChange({ ...scope, from, to: next });

  // After choosing From, keep the same month visible and let the next click choose To.
  // Choosing To before From swaps the endpoints rather than refusing the date.
  const pickDay = (day: string) => {
    if (end === "from") {
      setRange(day, scope.to && day > scope.to ? day : scope.to);
      setEnd("to");
    } else if (scope.from && day < scope.from) setRange(day, scope.from);
    else setRange(scope.from, day);
  };

  // A preset answers both boxes at once, but it is still an edit of the range being built: the
  // chip updates in place and the popover stays up so the pick can be seen and adjusted.
  const applyLastDays = (days: number) => setRange(lastDaysFrom(days), today);

  const navClass =
    "flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-dim outline-none transition hover:bg-muted-surface hover:text-text";

  const boxClass = (selected: boolean) =>
    clsx(
      "w-full cursor-pointer rounded-md border px-2 py-1.5 text-left text-sm outline-none transition",
      selected
        ? "border-text-mid bg-text-mid/5 text-text ring-2 ring-text-mid/20"
        : "border-outline bg-surface text-text hover:bg-muted-surface",
    );

  return (
    <div className="space-y-2.5">
      {/* The shortcuts lead: most date scopes are "recently", and the boxes below are for the
          window that is not. */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {DATE_PRESETS.map(days => (
            <button
              key={days}
              type="button"
              onClick={() => applyLastDays(days)}
              className="cursor-pointer rounded-full border border-outline px-2.5 py-1 text-xs text-text-mid transition hover:bg-muted-surface hover:text-text"
            >
              {t("last_n_days", { count: days })}
            </button>
          ))}
        </div>
      </div>
      <div ref={boxRowRef} className="grid grid-cols-2 gap-2 border-t border-outline pt-2.5">
        <div className="space-y-1">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-text-dim">
            {t("from")}
          </span>
          <button
            type="button"
            onClick={() => openEnd("from")}
            className={boxClass(end === "from")}
          >
            {scope.from ? formatDayKey(scope.from) : t("scope_date_any")}
          </button>
        </div>
        <div className="space-y-1">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-text-dim">
            {t("to")}
          </span>
          <button type="button" onClick={() => openEnd("to")} className={boxClass(end === "to")}>
            {formatDayKey(to)}
          </button>
        </div>
      </div>

      {/* Keep the calendar open while selecting and adjusting both ends of the range. */}
      {end && (
        <div ref={calendarRef} className="space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-text">
              {new Date(view.year, view.month, 1).toLocaleDateString(undefined, {
                month: "long",
                year: "numeric",
              })}
            </span>
            {/* Months run left to right, so the arrows do too, and every month is reachable — the
              process's own span guides with dots rather than locking the grid down. */}
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                aria-label={t("previous_month")}
                className={navClass}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                aria-label={t("next_month")}
                className={navClass}
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-y-0.5">
            {weekdays.map((label, position) => (
              <span
                key={`${position}-${label}`}
                className="pb-1 text-center text-[10px] font-semibold uppercase text-text-dim"
                aria-hidden="true"
              >
                {label}
              </span>
            ))}
            {cells.map((day, position) => {
              if (!day) {
                // biome-ignore lint/suspicious/noArrayIndexKey: Empty calendar cells occupy fixed positions.
                return <span key={`gap-${position}`} />;
              }
              const count = recorded.get(day) ?? 0;
              const start = day === scope.from;
              // An empty "to" still has a visible end — the box reads "today", so the grid closes the
              // range there too rather than trailing off with nothing marked.
              const finish = day === to;
              const inRange = !!scope.from && day >= scope.from && day <= to;
              const edge = start || finish;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => pickDay(day)}
                  aria-pressed={inRange || edge}
                  title={count > 0 ? t("n_instances", { count }) : undefined}
                  className={clsx(
                    "relative flex h-8 cursor-pointer items-center justify-center text-sm tabular-nums outline-none transition",
                    inRange && !edge && "bg-text-mid/10 text-text",
                    edge && "bg-text-mid font-semibold text-white",
                    start && "rounded-l-md",
                    // The end of a range is the half people misread, so it is drawn as a closing
                    // bracket — ringed and capped — rather than as a second, identical start.
                    finish && "rounded-r-md ring-2 ring-inset ring-white/70",
                    !inRange && !edge && "rounded-md hover:bg-muted-surface",
                    !edge && (count > 0 ? "font-medium text-text" : "text-text-dim"),
                  )}
                >
                  {parseDayKey(day).getDate()}
                  {edge && (
                    <span className="absolute inset-x-0 bottom-0.5 text-center text-[8px] font-bold uppercase leading-none tracking-wide text-white/80">
                      {start ? t("from") : t("to")}
                    </span>
                  )}
                  {count > 0 && !edge && (
                    <span className="absolute inset-x-0 bottom-0.5 flex justify-center gap-px">
                      {Array.from({ length: Math.min(count, MAX_DAY_DOTS) }, (_, dot) => (
                        <span
                          // biome-ignore lint/suspicious/noArrayIndexKey: Decorative dots have no identity or state.
                          key={dot}
                          className="h-1 w-1 rounded-full bg-text-mid"
                          aria-hidden="true"
                        />
                      ))}
                    </span>
                  )}
                  {day === today && !edge && (
                    <span
                      className="absolute inset-x-1.5 bottom-0 h-px bg-text-dim"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* The dots are the only thing on the grid that is not the range being edited, so they
              say what they are rather than leaving it to be inferred from where they cluster. */}
          <div className="flex items-center gap-2 text-[11px] text-text-dim">
            <span className="h-1 w-1 shrink-0 rounded-full bg-text-mid" aria-hidden="true" />
            <span className="min-w-0">{t("scope_date_dots_legend")}</span>
          </div>
        </div>
      )}

      {/* Confirming belongs to the editor, not to the calendar inside it: a range set from a
          preset never opens a month, and a footer that appears only once one is open leaves that
          much more common case with no way to say it is finished. */}
      <EditorFooter onPicked={onPicked} confirmLabel={confirmLabel} />
    </div>
  );
};

/**
 * The mark on every "describe it in words" control. Filled rather than outlined: at this size the
 * star only reads as itself as a solid shape, and the accent colour is what separates it from the
 * plain-text controls around it.
 */
const ScopeSparkle = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={clsx("fill-current", className)} aria-hidden="true">
    {/* Lucide's Sparkle has soft, wide arms; the spikes here run almost to the centre so the mark
        stays recognisably a four-point star at 20px. */}
    <path d="M12 1.5q.7 9.8 10.5 10.5Q12.7 12.7 12 22.5Q11.3 12.7 1.5 12Q11.3 11.3 12 1.5Z" />
  </svg>
);

// Submitted language filters resolve to run IDs; ordinary search stays local.
export const QueryEditor = ({
  scope,
  status,
  autoFocus = true,
  onChange,
  onResolve,
  onReset,
  onDone,
}: {
  scope: InstanceScope;
  status: ScopeQueryStatus;
  /**
   * Off where the field was merely revealed rather than asked for — a panel that opens on hover
   * would otherwise take the caret off whatever the pointer happened to pass over.
   */
  autoFocus?: boolean;
  onChange: (scope: InstanceScope) => void;
  onResolve: (text: string) => Promise<InstanceScopeQuery | null>;
  onReset: () => void;
  /** Called once the condition resolved and its chip landed — never on a failed attempt. */
  onDone: () => void;
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(scope.query?.text ?? "");
  useEffect(() => setText(scope.query?.text ?? ""), [scope.query?.text]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  useEffect(() => () => onReset(), [onReset]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Opening the picker focuses the language field, preserving any applied condition.
  useEffect(() => {
    if (!autoFocus) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [autoFocus]);

  const loading = status === "loading";
  const message = {
    idle: null,
    loading: t("filtering_instances"),
    error: t("filter_failed"),
    no_matches: t("no_instances_match_condition"),
  }[status];

  return (
    <div className="space-y-1.5" data-tour="nl-filter">
      <form
        onSubmit={async event => {
          event.preventDefault();
          const trimmed = text.trim();
          if (!trimmed || loading) return;
          const resolved = await onResolve(trimmed);
          // Nothing closes on a failure: the wording stays put with the reason under it, because
          // a chip that appears and then vanishes explains far less than a field that waited.
          if (!resolved) return;
          onChange({ ...currentScope.current, query: resolved });
          onDone();
        }}
        className="flex h-9 w-full min-w-0 items-center overflow-hidden rounded-lg border border-outline bg-surface transition focus-within:border-text-mid focus-within:ring-2 focus-within:ring-text-mid/20"
      >
        <ScopeSparkle className="ml-2.5 h-5 w-5 shrink-0 text-text-mid" />
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={event => {
            setText(event.target.value);
            if (status !== "idle") onReset();
          }}
          aria-label={t("processPage.plainLanguage")}
          placeholder={t("describe_instances_you_want")}
          disabled={loading}
          className="h-full min-w-0 flex-1 bg-transparent px-2 text-sm text-text placeholder:text-text-dim focus:outline-none disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={loading || !text.trim()}
          aria-label={t("filter_action")}
          className="mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-text-mid text-white transition disabled:opacity-40"
        >
          <Send className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </form>
      {message && (
        <p className="text-xs text-text-dim" aria-live="polite">
          {message}
        </p>
      )}
    </div>
  );
};
